import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, MessageSquare, Bug, HelpCircle, Flag, 
  Send, CheckCircle, Camera, X, Loader2 
} from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_TYPES = [
  { id: 'feedback', label: 'Feedback', icon: MessageSquare, description: 'Share your thoughts and suggestions' },
  { id: 'support', label: 'Support', icon: HelpCircle, description: 'Get help with your account or app issues' },
  { id: 'bug_report', label: 'Bug Report', icon: Bug, description: 'Report a technical issue or bug' },
  { id: 'user_report', label: 'Report User', icon: Flag, description: 'Report inappropriate behavior' }
];

const Contact = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportedUserId = searchParams.get('user');
  const reportedUserName = searchParams.get('name');
  
  const [selectedType, setSelectedType] = useState(reportedUserId ? 'user_report' : null);
  const [subject, setSubject] = useState(reportedUserId ? `Report: ${reportedUserName || 'User'}` : '');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleScreenshotChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Screenshot must be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setScreenshot(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedType) {
      toast.error('Please select a submission type');
      return;
    }
    
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    
    if (!message.trim()) {
      toast.error('Please enter your message');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact/submit`, {
        type: selectedType,
        subject: subject.trim(),
        message: message.trim(),
        reported_user_id: reportedUserId || null,
        screenshot_data: screenshot
      });
      
      setSubmitted(true);
      toast.success('Your submission has been received!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] pb-20">
        <header className="sticky top-0 z-40 glass">
          <div className="flex items-center gap-3 px-4 py-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-[#FF2E63]">Contact Us</h1>
          </div>
        </header>

        <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-[#08D9D6]/20 flex items-center justify-center mb-6">
            <CheckCircle className="w-10 h-10 text-[#08D9D6]" />
          </div>
          <h2 className="text-2xl font-bold text-[#EDEDED] mb-3">Thank You!</h2>
          <p className="text-[#A1A1AA] mb-8 max-w-sm">
            Your submission has been received. Our team will review it and get back to you if needed.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => {
                setSubmitted(false);
                setSelectedType(null);
                setSubject('');
                setMessage('');
                setScreenshot(null);
              }}
              variant="outline"
              className="border-white/20 text-[#EDEDED] hover:bg-white/10"
            >
              Submit Another
            </Button>
            <Button
              onClick={() => navigate('/grid')}
              className="bg-[#FF2E63] hover:bg-[#E01E50] text-white"
            >
              Back to Browse
            </Button>
          </div>
        </div>

        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-20">
      <header className="sticky top-0 z-40 glass">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold text-[#FF2E63]">Contact Us</h1>
        </div>
      </header>

      <div className="px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Type Selection */}
          <div className="space-y-3">
            <Label className="text-[#A1A1AA] text-sm">What can we help you with?</Label>
            <div className="grid grid-cols-2 gap-3">
              {CONTACT_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = selectedType === type.id;
                return (
                  <button
                    key={type.id}
                    type="button"
                    data-testid={`type-${type.id}`}
                    onClick={() => setSelectedType(type.id)}
                    className={`p-4 rounded-xl text-left transition-all ${
                      isSelected
                        ? 'bg-[#FF2E63]/20 border-2 border-[#FF2E63]'
                        : 'bg-white/5 border-2 border-transparent hover:border-white/20'
                    }`}
                  >
                    <Icon className={`w-6 h-6 mb-2 ${isSelected ? 'text-[#FF2E63]' : 'text-[#A1A1AA]'}`} />
                    <p className={`font-semibold ${isSelected ? 'text-[#FF2E63]' : 'text-[#EDEDED]'}`}>
                      {type.label}
                    </p>
                    <p className="text-xs text-[#52525B] mt-1">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Reported User Info */}
          {reportedUserId && selectedType === 'user_report' && (
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-[#A1A1AA]">Reporting user:</p>
              <p className="text-[#EDEDED] font-semibold">{reportedUserName || reportedUserId}</p>
            </div>
          )}

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject" className="text-[#A1A1AA] text-sm">Subject</Label>
            <Input
              id="subject"
              data-testid="subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief description of your issue"
              className="bg-white/5 border-white/10 text-white placeholder:text-[#52525B]"
              maxLength={100}
            />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message" className="text-[#A1A1AA] text-sm">Message</Label>
            <Textarea
              id="message"
              data-testid="message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Please provide as much detail as possible..."
              className="bg-white/5 border-white/10 text-white placeholder:text-[#52525B] min-h-[150px]"
              maxLength={2000}
            />
            <p className="text-xs text-[#52525B] text-right">{message.length}/2000</p>
          </div>

          {/* Screenshot */}
          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-sm">Screenshot (optional)</Label>
            {screenshot ? (
              <div className="relative">
                <img 
                  src={screenshot} 
                  alt="Screenshot preview" 
                  className="w-full max-h-48 object-contain rounded-lg bg-white/5"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setScreenshot(null)}
                  className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#FF2E63]/50 transition-colors">
                <Camera className="w-5 h-5 text-[#A1A1AA]" />
                <span className="text-sm text-[#A1A1AA]">Attach a screenshot</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleScreenshotChange}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            data-testid="submit-contact-btn"
            disabled={submitting || !selectedType}
            className="w-full bg-[#FF2E63] hover:bg-[#E01E50] text-white h-12 text-lg"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Submit
              </>
            )}
          </Button>
        </form>
      </div>

      <BottomNav />
    </div>
  );
};

export default Contact;
